import { CheckCircle, Clock, PlayCircle } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { getApplicableTestCases } from "../data/testCaseTemplates";
import type { ProductType, TestCase, TestResult, Wallet } from "../types";
import { WarningIcon } from "./icons/WarningIcon";
import { PartnerResultsView } from "./PartnerResultsView";
import { TestInstructionsView } from "./TestInstructionsView";
import { TestResultsForm } from "./TestResultsForm";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

interface TestingSectionProps {
	wallet: Wallet;
	isUpdating?: boolean;
	onSubmitResult: (result: TestResult) => void;
}

export const TestingSection: React.FC<TestingSectionProps> = ({
	wallet,
	isUpdating = false,
	onSubmitResult,
}) => {
	const [selectedTestCase, setSelectedTestCase] = useState<TestCase | null>(
		null,
	);
	const [selectedResult, setSelectedResult] = useState<TestResult | null>(null);
	const [showResultsForm, setShowResultsForm] = useState(false);

	// Get applicable test cases based on wallet products
	const getProductType = (): ProductType | undefined => {
		if (!wallet.products || wallet.products.length === 0) return undefined;

		const hasDeposit = wallet.products.includes("Deposit");
		const hasWithdraw = wallet.products.includes("Withdraw");

		if (hasDeposit && hasWithdraw) return "both";
		if (hasDeposit) return "deposit";
		if (hasWithdraw) return "withdraw";
		return undefined;
	};

	const productType = getProductType();
	const applicableTestCases = productType
		? getApplicableTestCases(productType)
		: [];

	// Check if a test has been completed
	const isTestCompleted = (testCaseId: string) => {
		return wallet.test_results?.some((tr) => tr.test_case_id === testCaseId);
	};

	const handleStartTest = (testCase: TestCase) => {
		// Check if test already has a result
		const existingResult = wallet.test_results?.find(
			(tr) => tr.test_case_id === testCase.id,
		);

		if (existingResult) {
			// If result exists, show it (partner can view/edit if not certified)
			setSelectedResult(existingResult);
			setSelectedTestCase(testCase);
			setShowResultsForm(existingResult.status !== "certified"); // Can edit if not certified
		} else {
			// No result yet, show instructions
			setSelectedTestCase(testCase);
			setShowResultsForm(false);
		}
	};

	const handleSubmitResults = (testCase: TestCase) => {
		setSelectedTestCase(testCase);
		setShowResultsForm(true);
	};

	if (showResultsForm && selectedTestCase) {
		return (
			<TestResultsForm
				testCase={selectedTestCase}
				wallet={wallet}
				existingResult={selectedResult || undefined}
				isSubmitting={isUpdating}
				onSubmit={(result) => {
					onSubmitResult(result);
					setShowResultsForm(false);
					setSelectedTestCase(null);
					setSelectedResult(null);
				}}
				onCancel={() => {
					setShowResultsForm(false);
					setSelectedTestCase(null);
					setSelectedResult(null);
				}}
			/>
		);
	}

	if (selectedResult && selectedTestCase && !showResultsForm) {
		return (
			<PartnerResultsView
				result={selectedResult}
				testCase={selectedTestCase}
				onBack={() => {
					setSelectedResult(null);
					setSelectedTestCase(null);
				}}
				onEdit={() => setShowResultsForm(true)}
			/>
		);
	}

	if (selectedTestCase) {
		return (
			<TestInstructionsView
				testCase={selectedTestCase}
				wallet={wallet}
				onSubmitResults={() => handleSubmitResults(selectedTestCase)}
				onBack={() => setSelectedTestCase(null)}
			/>
		);
	}

	return (
		<div className="mt-4">
			{!productType ? (
				<div className="rounded-xl border border-[#F59E0B]/35 bg-[#F59E0B]/10 p-4 text-(--color-foreground)">
					<div className="mb-2 flex items-center gap-2">
						<WarningIcon size={20} className="text-[#F59E0B]" />
						<strong>No Products Configured</strong>
					</div>
					<p className="m-0 text-sm text-(--color-muted-foreground)">
						Configure wallet products (Deposit/Withdraw) to see test cases.
					</p>
				</div>
			) : (
				<div className="flex flex-col gap-2">
					{applicableTestCases.map((testCase) => {
						const isCompleted = isTestCompleted(testCase.id);
						const result = wallet.test_results?.find(
							(tr) => tr.test_case_id === testCase.id,
						);
						const isCertified = result?.status === "certified";

						return (
							<div
								key={testCase.id}
								className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-colors ${
									isCertified
										? "border-[#00E6C3]/30 bg-[#00E6C3]/5"
										: "border-(--color-border) bg-(--color-card) hover:border-(--color-accent-border)"
								}`}
							>
								<div className="flex-1">
									<div className="flex items-center gap-2">
										{isCertified ? (
											<CheckCircle size={16} className="text-[#00E6C3]" />
										) : (
											<Clock
												size={16}
												className="text-(--color-muted-foreground)"
											/>
										)}
										<span className="font-medium text-sm text-(--color-foreground)">
											{testCase.name}
										</span>
										{testCase.isRequired && (
											<Badge variant="warning">Required</Badge>
										)}
									</div>
								</div>
								<Button
									type="button"
									size="sm"
									onClick={() => handleStartTest(testCase)}
								>
									<PlayCircle size={16} />
									{isCompleted ? "View Results" : "Start Testing"}
								</Button>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
